import type {
  AndExpression,
  AssignmentRule,
  OrExpression,
  RuleExpression,
} from '@tsagko/label-contracts'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAdapter } from '../adapters/AdapterProvider'
import { useCmdKConsumer } from '../shell/CmdKContext'
import { JsonView } from './JsonView'
import { RuleEditor } from './RuleEditor'
import { normaliseToTopGroup } from './rule-ops'

function makeBlankRule(id: string): AssignmentRule {
  return {
    id,
    name: 'Untitled rule',
    condition: { op: 'and', operands: [] },
    assignments: {},
  }
}

function toTopGroup(expr: RuleExpression): AndExpression | OrExpression {
  if (expr.op === 'and' || expr.op === 'or') return expr
  return normaliseToTopGroup(expr)
}

export function RuleConfiguratorRoute() {
  // Enable the global Cmd+K shortcut while this route is mounted — it is the
  // only route that has CmdK consumers (FilterRow's "⌘K" field-picker button).
  useCmdKConsumer()

  const adapter = useAdapter()
  const navigate = useNavigate()
  const { ruleId } = useParams<{ ruleId: string }>()

  const initialRule = useMemo(
    () => makeBlankRule(ruleId && ruleId !== 'new' ? ruleId : `draft-${Date.now()}`),
    [ruleId]
  )

  const [rule, setRule] = useState<AssignmentRule>(initialRule)
  const [etag, setEtag] = useState<string | undefined>()
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>(
    'idle'
  )
  const [saveError, setSaveError] = useState<string | undefined>()

  useEffect(() => {
    if (!adapter || !ruleId || ruleId === 'new') return
    let cancelled = false
    void (async () => {
      const res = await adapter.resources.loadRule(ruleId)
      if (cancelled) return
      if (res.status === 'ok') {
        setRule(res.data.resource)
        setEtag(res.data.etag)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [adapter, ruleId])

  // RuleEditor expects a top-level group. Wrap a bare CompareExpression if needed.
  const editorExpression = useMemo(() => toTopGroup(rule.condition), [rule.condition])

  const onExpressionChange = useCallback(
    (next: AndExpression | OrExpression) => {
      setRule(prev => ({ ...prev, condition: next }))
    },
    []
  )

  const onJsonChange = useCallback((next: AndExpression | OrExpression) => {
    setRule(prev => ({ ...prev, condition: next }))
  }, [])

  const onRename = useCallback((name: string) => {
    setRule(prev => ({ ...prev, name }))
  }, [])

  const onSave = useCallback(async () => {
    if (!adapter) return
    setSaveStatus('saving')
    setSaveError(undefined)
    const res = await adapter.resources.saveRule(
      rule,
      etag ? { ifMatch: etag } : undefined
    )
    if (res.status === 'ok') {
      setEtag(res.data.etag)
      setSaveStatus('saved')
      if (!ruleId || ruleId === 'new') {
        navigate(`/rules/${encodeURIComponent(rule.id)}`, { replace: true })
      }
      setTimeout(() => setSaveStatus('idle'), 2000)
    } else {
      setSaveStatus('error')
      setSaveError(res.error.message)
    }
  }, [adapter, rule, etag, ruleId, navigate])

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-stone-200 bg-white px-6 py-3 dark:border-stone-800 dark:bg-stone-900">
        <div className="flex flex-col">
          <h2 className="sr-only">Rules</h2>
          <input
            value={rule.name}
            onChange={e => onRename(e.target.value)}
            data-testid="rc-name"
            aria-label="Rule name"
            className="bg-transparent text-lg font-semibold outline-none focus:rounded focus:bg-stone-100 focus:px-2 dark:focus:bg-stone-800"
          />
          <p className="text-xs text-stone-500 dark:text-stone-400">
            Assignment rule · id: <code>{rule.id}</code>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="rc-save"
            onClick={onSave}
            disabled={!adapter || saveStatus === 'saving'}
            className="rounded bg-sky-600 px-3 py-1 text-xs text-white disabled:opacity-40 hover:enabled:bg-sky-700"
          >
            {saveStatus === 'saving'
              ? 'Saving…'
              : saveStatus === 'saved'
                ? 'Saved ✓'
                : 'Save rule'}
          </button>
        </div>
      </div>
      {saveError && (
        <div className="bg-rose-50 px-6 py-2 text-xs text-rose-700 dark:bg-rose-950 dark:text-rose-300">
          {saveError}
        </div>
      )}
      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-auto p-6">
          <RuleEditor expression={editorExpression} onChange={onExpressionChange} />
        </div>
        <aside className="flex w-96 shrink-0 flex-col overflow-auto border-l border-stone-200 bg-white p-4 dark:border-stone-800 dark:bg-stone-900">
          <div data-testid="rc-json-view">
            <JsonView value={editorExpression} onChange={onJsonChange} />
          </div>
        </aside>
      </div>
    </div>
  )
}
